import { useEffect, useRef } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
  ContentAccessUser,
  ContentWorkflow,
  UserProfile,
} from '@amzn/awscat-pattern-library-service-model';

import RouterPaths from '../../common/RouterPaths';
import { ActiveRole, useCurrentUser } from '../../contexts/UserContext';
import { LEGACY_REDIRECT_STATES } from '../v2-common/LegacyWorkflowStates';
import { useLoadedDocument } from './useLoadedDocument';

/**
 * Redirects to the legacy content viewer when the loaded draft is in a
 * deprecated peer-review state that v2 will never support.
 */
export function useLegacyWorkflowRedirect(): boolean {
  const document = useLoadedDocument();
  const { contentId } = useParams<{ contentId: string }>();
  const { userId, activeRole } = useCurrentUser();
  const history = useHistory();
  const hasRedirected = useRef(false);

  const redirectTarget =
    document?.kind === 'draft'
      ? computeRedirectTarget(
          document.workflow,
          document.author,
          document.coAuthors,
          contentId,
          userId,
          activeRole,
        )
      : undefined;

  useEffect(() => {
    if (redirectTarget && !hasRedirected.current) {
      hasRedirected.current = true;
      history.replace(redirectTarget);
    }
  }, [redirectTarget, history]);

  return redirectTarget !== undefined;
}

export function computeRedirectTarget(
  workflow: ContentWorkflow,
  author: UserProfile,
  coAuthors: readonly ContentAccessUser[],
  contentId: string,
  userId: string,
  activeRole: ActiveRole,
): string | undefined {
  if (!LEGACY_REDIRECT_STATES.has(workflow.draftState)) return undefined;

  // Authorship takes priority — a user who is both author and bar raiser sees the author view.
  const isAuthorOrCoAuthor = author.userId === userId || coAuthors.some((c) => c.userId === userId);
  if (isAuthorOrCoAuthor) {
    return generatePath(RouterPaths.AUTHOR_CONTENT_VIEWER, { contentId });
  }

  // Admin hat sees the admin content viewer.
  if (activeRole === 'admin') {
    return generatePath(RouterPaths.ADMIN_CONTENT_VIEWER, { contentId });
  }

  // Bar raiser hat sees the bar raiser content viewer.
  if (activeRole === 'bar_raiser') {
    return generatePath(RouterPaths.BAR_RAISER_CONTENT_VIEWER, { contentId });
  }

  // Otherwise we assume this is a reviewer.
  return generatePath(RouterPaths.REVIEWER_CONTENT_VIEWER, { contentId });
}
